import { ApiSdkEvents, TaskCollection, TaskState } from '@ekkogmbh/apisdk';
import { createContextHardened } from '../Helper/ContextHelper';
import { useCallback, useEffect, useRef, useState } from 'react';
import useRequest from '../Hooks/useRequest';
import useEkko from './EkkoProvider';
import { enqueueSnackbar } from 'notistack';
import useStableCallback from '../Hooks/useStableCallback';

export type TaskCollectionProgressCallback = (id: string, state: string, progress?: number) => void;

const stateEventMap: Record<TaskState, ApiSdkEvents> = {
  [TaskState.CREATED]: ApiSdkEvents.TASK_COLLECTION_CREATED,
  [TaskState.FINISHED]: ApiSdkEvents.TASK_COLLECTION_FINISHED,
  [TaskState.FAILED]: ApiSdkEvents.TASK_COLLECTION_FAILED,
  [TaskState.RUNNING]: ApiSdkEvents.TASK_COLLECTION_PROGRESS,
};
export const mapTaskStateToEvent = (state: TaskState): ApiSdkEvents =>
  stateEventMap[state] ?? ApiSdkEvents.TASK_COLLECTION_ERROR;

interface Context {
  taskCollections: TaskCollection[];
  /**
   * Subscribe to task collection progress polling.
   * URI is nullable for ApiSdk compliance.
   * When not providing a callback, the state is still polled.
   */
  subscribe: (uri: string | null, callback?: TaskCollectionProgressCallback) => void;
  unsubscribe: (uri: string) => void;
}

const [TaskCollectionContext, useTaskCollections] = createContextHardened<Context>('Task Collection');
export default useTaskCollections;

/** Bundles states per subscription */
interface TaskCollectionSubscription {
  taskCollection?: TaskCollection;
  id: number;
  callback?: TaskCollectionProgressCallback;
  updated?: EpochTimeStamp;
}

// delays in ms
const fetchDelay = 1000;

interface Props {
  children: React.ReactNode;
}
export const TaskCollectionProvider = ({ children }: Props): React.JSX.Element => {
  const { api } = useEkko();

  /** Subscriptions indexed by URI */
  const subscriptions = useRef<Record<string, TaskCollectionSubscription>>({});
  const intervalRef = useRef<NodeJS.Timeout>();
  const [isPolling, startPolling] = useRequest<TaskCollection>();
  const [taskCollections, setTaskCollections] = useState<TaskCollection[]>([]);

  const subscribe = useCallback(
    (uri: string | null, callback?: TaskCollectionProgressCallback) => {
      if (uri === null || subscriptions.current?.[uri] !== undefined) {
        return;
      }

      const uriParts = uri.split('/');
      const idPart = uriParts[uriParts.length - 1];
      const taskCollectionId = parseInt(idPart, 10);

      if (isNaN(taskCollectionId)) {
        enqueueSnackbar('Could not queue task collection id: ' + taskCollectionId, { variant: 'error' });
        return;
      }

      const sub: TaskCollectionSubscription = {
        id: taskCollectionId,
        callback,
      };

      subscriptions.current[uri] = sub;
    },
    [api],
  );

  const unsubscribe = useCallback((uri: string) => {
    delete subscriptions.current[uri];
    updateTaskCollections();
  }, []);

  const pollTaskCollection = useCallback(
    (uri: string) => {
      if (isPolling) {
        return;
      }

      const sub = subscriptions.current?.[uri] ?? undefined;
      if (sub === undefined) {
        return;
      }

      startPolling(
        () => api.getTaskCollectionProgress(sub.id),
        (tc?: TaskCollection) => {
          if (tc === undefined) {
            unsubscribe(uri);
          } else {
            if ([TaskState.FINISHED, TaskState.FAILED].includes(tc.state)) {
              // stop polling concluded states
              unsubscribe(uri);
            } else {
              // update local ref
              sub.taskCollection = tc;
              sub.updated = Date.now();
              subscriptions.current[uri] = sub;
              updateTaskCollections();
            }

            if (sub.callback !== undefined) {
              sub.callback(tc.id, tc.state, tc.progress);
            }

            // publish to apiSdk events
            api.emit(mapTaskStateToEvent(tc.state), tc);
          }
        },
        () => {
          // emit error?
          unsubscribe(uri);
        },
      );
    },
    [isPolling, api],
  );

  // Pulls task collections from subscription ref into state
  const updateTaskCollections = useCallback(() => {
    const currentCollections = Object.values(subscriptions.current).reduce((carry: TaskCollection[], sub) => {
      if (sub.taskCollection !== undefined) {
        carry.push(sub.taskCollection);
      }

      return carry;
    }, []);

    setTaskCollections(currentCollections);
  }, []);

  const cycleSubscriptions = useStableCallback(() => {
    if (isPolling) {
      // skip cycle if still polling something
      return;
    }

    let nextUri: string | undefined;
    let nextSub: TaskCollectionSubscription | undefined;
    for (const [uri, sub] of Object.entries(subscriptions.current)) {
      // any sub that hasn't been polled yet wins
      if (sub.updated === undefined) {
        nextUri = uri;
        nextSub = sub;
        break;
      }

      if (nextSub === undefined || sub.updated > nextSub.updated!) {
        nextSub = sub;
        nextUri = uri;
      }
    }

    if (nextUri !== undefined) {
      pollTaskCollection(nextUri);
    }
  }, [isPolling]);

  useEffect(() => {
    intervalRef.current = setInterval(cycleSubscriptions, fetchDelay);
    return () => {
      subscriptions.current = {};
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <TaskCollectionContext.Provider
      value={{
        taskCollections,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </TaskCollectionContext.Provider>
  );
};
